// packages/shared/src/wallet/errors.ts
//
// Billing error code namespace + canonical API error envelope.
//
// Node-pure. Safe to bundle in Vite client builds.
//
// Tree-shake note: `BillingErrorCode` is a frozen `const` object created once
// at module load — no I/O, no side effects beyond the dictionary allocation.
// Bundlers (Rollup/Vite/esbuild) eliminate unused keys via dead-code elimination
// when callers reference specific named properties.
// ---------------------------------------------------------------------------
// BillingErrorCode — SCREAMING_SNAKE_CASE namespaced error codes
//
// Namespace prefixes by module boundary:
//   BILLING_  — wallet / balance / ledger
//   INVOICE_  — invoice mode / billing plan
//   AUTH_     — authentication / authorization (shared, added as needed)
//   WALLET_   — reserved for wallet-specific cross-cutting codes
//
// NOTE: importers should reference specific keys, not the whole object,
// to maximise tree-shake effectiveness.
// ---------------------------------------------------------------------------
/**
 * Frozen dictionary of billing-related error codes.
 *
 * Values equal their keys — allows `switch (code)` with full TS narrowing.
 * Extend this dictionary (never delete) to maintain backward compatibility
 * with clients that may have cached the code for i18n lookup.
 *
 * @example
 *   throw makeApiError(BillingErrorCode.BILLING_INSUFFICIENT_BALANCE, "Số dư không đủ");
 */
export const BillingErrorCode = Object.freeze({
    // --- Wallet / balance -----------------------------------------
    /** Withdraw or charge amount exceeds available balance. */
    BILLING_INSUFFICIENT_BALANCE: "BILLING_INSUFFICIENT_BALANCE",
    /** Merchant wallet is in a state that disallows withdrawals. */
    BILLING_WITHDRAW_DISABLED: "BILLING_WITHDRAW_DISABLED",
    /** Merchant account is suspended — all billing operations blocked. */
    BILLING_MERCHANT_SUSPENDED: "BILLING_MERCHANT_SUSPENDED",
    /**
     * Ledger integrity check failed — DR total != CR total for a TxGroup.
     * Triggers automatic reconciliation job and alerts on-call.
     */
    BILLING_LEDGER_UNBALANCED: "BILLING_LEDGER_UNBALANCED",
    // --- Throttling ------------------------------------------------
    /**
     * Per-user per-action rate limit exceeded (Redis sliding window).
     * HTTP response includes `Retry-After` header (seconds).
     * Client message: "Quá nhiều yêu cầu. Thử lại sau X giây."
     */
    BILLING_RATE_LIMITED: "BILLING_RATE_LIMITED",
    // --- Validation / query ----------------------------------------
    /** Transaction history filter fields failed validation. */
    BILLING_INVALID_FILTER: "BILLING_INVALID_FILTER",
    /**
     * Pagination cursor is malformed or expired.
     * Clients should restart from page 1 (no cursor).
     */
    BILLING_INVALID_CURSOR: "BILLING_INVALID_CURSOR",
    // --- Cross-module: Invoice -----------------------------------------------
    /**
     * Requested invoice billing mode is not available for this merchant tier.
     * Cross-module error using INVOICE_ prefix naming rules.
     */
    INVOICE_MODE_NOT_AVAILABLE: "INVOICE_MODE_NOT_AVAILABLE",
});
// ---------------------------------------------------------------------------
// makeApiError — builder for ApiErrorEnvelope (canonical helper)
//
// Task 11 (api-response.ts) should import this function rather than
// re-implementing the envelope logic.
// ---------------------------------------------------------------------------
/**
 * Build a canonical API error envelope.
 *
 * @param code      SCREAMING_SNAKE_CASE error code (use `BillingErrorCode` constants).
 * @param message   EN fallback message. Client i18n keyed off `code`.
 * @param opts.details   Optional structured detail map (field errors, context).
 * @param opts.requestId Per-request correlation ID (pass `request.id` on server;
 *                       synthesize with `crypto.randomUUID()` on client if needed).
 *                       Defaults to empty string when omitted.
 *
 * @example
 *   reply.status(402).send(
 *     makeApiError(
 *       BillingErrorCode.BILLING_INSUFFICIENT_BALANCE,
 *       "Insufficient balance to complete withdrawal",
 *       { requestId: request.id, details: { available: 500, required: 1000 } },
 *     ),
 *   );
 */
export function makeApiError(code, message, opts) {
    return {
        success: false,
        data: null,
        error: {
            code,
            message,
            details: opts?.details,
            requestId: opts?.requestId ?? "",
        },
    };
}
//# sourceMappingURL=errors.js.map